import {IProvider} from "./provider/provider";
import {GA4Provider} from "./provider/g-a-4-provider";
import {TAnalyticProvider} from "services-comun-meteored/modules/services/cmp/interface";
import {AndroidProvider} from "./provider/android-provider";
import {IOSProvider} from "./provider/i-o-s-provider";
import Metadata from "../metadata";
import {site} from "../context/context";
import {log} from "../util/log";
import {EventManager} from "../api/event-manager";
import {AnalyticEvent} from "./custom-impl/analytic-event";
import {EAction, ELabel, EName} from "./event-definition";
import {isBot} from "services-comun/modules/browser/user-agent";

export const COMPUTED_EVENT_NAME = (): string => {
    if (isBot()) {
        return EName.CMP_BOT;
    }
    return EName.CMP;
}


export const COMPUTED_LABEL_VALOR = (): string => {
    return `valor_${Metadata().acceptanceLevel}`;
}

const ADD_TEST_PART = (base: string): string => {
    let label = base;
    const testPart = site.testABApplied()?.map(test => `${test.name}_${test.spec.name}`).join('_');

    if (testPart) {
        label = `${label}_${testPart}`;
    }

    return label;
}

export interface IEvent {
    name?: string;
    action: string;
    label: string;
}

/**
 * Eventos de test A/B que se registran.
 */
const TEST_AB_REPORT_ENABLED: Map<string, string[]> = new Map<string, string[]>(
    [
        [
            EAction.GDPR, [
                ELabel.APARECE,
                ELabel.SOCIOS,
                ELabel.CONFIGURACION,
                ELabel.YA_ACEPTADO,
                ELabel.ACEPTAR,
                ELabel.RECHAZAR,
                ELabel.APARECE_GLOBAL,
                ELabel.ACEPTAR_GLOBAL,
                ELabel.RECHAZAR_GLOBAL,
            ]
        ]
    ]
);

export class Event {
    /* STATIC */
    public static create(data: IEvent): Event {
        return new Event({
            name: COMPUTED_EVENT_NAME(),
            ...data
        });
    }

    /**
     * Crea el evento de test A/B si procede.
     * Sólo se crea si aplica algún test A/B y el evento está habilitado para reportar.
     * @param data Datos del evento.
     */
    public static createTest(data: IEvent): Event|null {
        if (site.testABApplied().length
            && TEST_AB_REPORT_ENABLED.has(data.action)
            && TEST_AB_REPORT_ENABLED.get(data.action)?.includes(data.label))
        {
            return new Event({
                name: COMPUTED_EVENT_NAME(),
                action: data.action,
                label: ADD_TEST_PART(data.label)
            });
        }
        return null;
    }

    /* INSTANCE */
    private constructor(private readonly _data: IEvent) {
    }

    private get data(): IEvent {
        return this._data;
    }

    public get name(): string {
        return this.data.name ?? COMPUTED_EVENT_NAME();
    }

    public get action(): string {
        return this.data.action;
    }

    public get label(): string {
        return this.data.label;
    }

    public register(type: TAnalyticProvider): void {
        let parser: IProvider<any>|undefined = undefined;
        switch (type) {
            case TAnalyticProvider.GA4:
                parser = new GA4Provider();
                break;
            case TAnalyticProvider.ANDROID:
                parser = new AndroidProvider();
                break;
            case TAnalyticProvider.IOS:
                parser = new IOSProvider();
                break;
        }

        if (parser) {
            parser.send(parser.parse(this));
            log.debug(`Lanzado evento ${this.action} - ${this.label}`);
        } else {
            log.debug(`No se ha podido lanzar el evento ${this.action} - ${this.label} porque no se ha encontrado el parser para el tipo ${type}`);
        }

        // Notificamos el evento
        this.notify();

        // Registramos el evento custom
        this.registerCustom();
    }

    private notify(): void {
        EventManager.trigger(EventManager.EVENT_CMP_ANALYTIC, this.data);
    }

    private registerCustom(): void {
        AnalyticEvent.build(this).send();
    }
}
